<template>
  <span
    :style="styleRoot">
    <transition
      :name="transition"
      @after-enter="handleAfterEnter"
      @after-leave="handleAfterLeave">
      <div
        v-show="!disabled && showPopper"
        :id="tooltipId"
        ref="popper"
        :aria-hidden="(disabled || !showPopper) ? 'true' : 'false'"
        class="s-popover s-popper"
        :class="[popperClass, content && 's-popover--plain']"
        role="tooltip"
        :style="{ width: width==='auto' ? width : width + 'px' }">
        <div
          v-if="title"
          class="s-popover__title"
          v-text="title"/>
        <slot>{{ content }}</slot>
      </div>
    </transition>
    <span
      ref="wrapper"
      v-clickoutside="clickoutside"
      class="s-popover__reference-wrapper"
      :style="styleWrapper">
      <slot
        name="reference"
        :show="showPopper"/>
    </span>
  </span>
</template>
<script>
import Popper from '../utils/vue-popper'
import { on, off, addClass } from '../utils/dom'
import { generateId } from '../utils/util'
import Clickoutside from '../utils/clickoutside'

/**
 * Всплывающее блоки
 * @displayName Popper
*/
export default {
  name: 'sPopover',
  mixins: [Popper],
  directives: { Clickoutside },
  props: {
    /**
     * как запускается всплывающее окно
     * @values click, hover, manual
     */
    trigger: {
      type: String,
      default: 'click',
      validator: value => ['click', 'hover', 'manual'].indexOf(value) > -1
    },
    /**
     * задержка перед появлением при наведении триггера, в миллисекундах
     */
    openDelay: {
      type: Number,
      default: 0
    },
    /**
     * задержка перед исчезновением при наведении триггера, в миллисекундах
     */
    closeDelay: {
      type: Number,
      default: 200
    },
    /**
     * Заголовок
     */
    title: {
      type: String,
    },
    /**
     * Признак отключения
     */
    disabled: {
      type: Boolean,
    },
    /**
     * всплывающее содержимое, может быть заменено стандартным 'slot'
     */
    content: {
      type: String,
    },
    /**
     * HTML элемент, который вызывает всплывающее окно
     */
    reference: {
      type: Object
    },
    popperClass: String,
    /**
     * ширина всплывающего окна
     */
    width: {
      type: [String, Number],
    },
    /**
     * Название анимации
     */
    transition: {
      type: String,
      default: 'fade-in-linear'
    },
    /**
     * tabindex of Popover
     */
    tabindex: {
      type: Number,
      default: 0
    },
    styleWrapper: {
      type: String
    },
    styleRoot: {
      type: String
    }
  },
  computed: {
    tooltipId() {
      return `s-popover-${generateId()}`
    }
  },
  watch: {
    showPopper(val) {
      if (this.disabled) {
        return
      }
      val ? this.$emit('show') : this.$emit('hide')
    }
  },
  mounted() {
    let reference = this.referenceElm = this.reference || this.$refs.reference
    if (!reference && this.$refs.wrapper.children) {
      reference = this.referenceElm = this.$refs.wrapper.children[0]
    }
    if (reference) {
      this.createPopover(reference)
    }
  },
  beforeDestroy() {
    this.cleanup()
  },
  deactivated() {
    this.cleanup()
  },
  methods: {
    clickoutside() {
      this.$emit('clickoutside')
    },
    createPopover(reference) {
      const popper = this.popper || this.$refs.popper
      addClass(reference, 's-popover__reference')
      reference.setAttribute('aria-describedby', this.tooltipId)
      reference.setAttribute('tabindex', this.tabindex)
      popper.setAttribute('tabindex', 0)
      
      on(reference, 'keydown', this.handleKeydown)
      if (this.trigger !== 'click') {
        on(reference, 'focusin', () => {
          this.handleFocus()
          const instance = reference.__vue__
          if (instance && typeof instance.focus === 'function') {
            instance.focus()
          }
        })
        on(popper, 'focusin', this.handleFocus)
      }
      if (this.trigger === 'click') {
        on(reference, 'click', this.doToggle)
        on(document, 'click', this.handleDocumentClick)
      } else if (this.trigger === 'hover') {
        on(reference, 'mouseenter', this.handleMouseEnter)
        on(popper, 'mouseenter', this.handleMouseEnter)
        on(reference, 'mouseleave', this.handleMouseLeave)
        on(popper, 'mouseleave', this.handleMouseLeave)
      } else if (this.trigger === 'focus') {
        if (this.tabindex < 0) {
          //console.warn('[Warn][Popover]a negative taindex means that the element cannot be focused by tab key')
        }
        if (reference.querySelector('input, textarea')) {
          on(reference, 'focusin', this.doShow)
          on(reference, 'focusout', this.doClose)
        } else {
          on(reference, 'mousedown', this.doShow)
          on(reference, 'mouseup', this.doClose)
        }
      }
    },
    doToggle() {
      if (this.disabled) {
        return
      }
      this.$emit('toggle', !this.showPopper)
      this.showPopper = !this.showPopper
    },
    doShow() {
      if (this.disabled) {
        return
      }
      this.showPopper = true
    },
    doClose() {
      if (this.disabled) {
        return
      }
      this.showPopper = false
      this.resetReferenceState()
    },
    resetReferenceState() {
      const reference = this.referenceElm || this.reference || this.$refs.reference
      if (reference) {
        reference.removeAttribute && reference.removeAttribute('tabindex')
        reference.removeAttribute && reference.removeAttribute('aria-describedby')
        reference.classList && reference.classList.remove('s-popover__reference')
      }
    },
    handleFocus() {
      if (this.disabled) {
        return
      }
      addClass(this.referenceElm, 'focusing')
      if (this.trigger === 'click' || this.trigger === 'focus') { this.showPopper = true }
    },
    handleMouseEnter() {
      if (this.disabled) {
        return
      }
      this.$emit('mouseenter')
      clearTimeout(this._timer)
      if (this.openDelay) {
        this._timer = setTimeout(() => {
          this.showPopper = true
        }, this.openDelay)
      } else {
        this.showPopper = true
      }
    },
    handleKeydown(ev) {
      if (this.disabled) {
        return
      }
      this.$emit('keydown', ev)
      if (ev.keyCode === 27 && this.trigger !== 'manual') { // esc
        this.doClose()
      }
    },
    handleMouseLeave() {
      if (this.disabled) {
        return
      }
      this.$emit('mouseleave')
      clearTimeout(this._timer)
      if (this.closeDelay) {
        this._timer = setTimeout(() => {
          this.showPopper = false
        }, this.closeDelay)
      } else {
        this.showPopper = false
      }
    },
    handleDocumentClick(e) {
      if (this.disabled) {
        return
      }
      this.$emit('click', e)
      let reference = this.reference || this.$refs.reference
      const popper = this.popper || this.$refs.popper
      if (!reference && this.$refs.wrapper.children) {
        reference = this.referenceElm = this.$refs.wrapper.children[0]
      }
      if (!this.$el ||
        !reference ||
        this.$el.contains(e.target) ||
        reference.contains(e.target) ||
        !popper ||
        popper.contains(e.target)) { return }
      this.showPopper = false
    },
    handleAfterEnter() {
      this.$emit('after-enter')
    },
    handleAfterLeave() {
      this.$emit('after-leave')
      this.resetReferenceState()
      this.doDestroy()
    },
    cleanup() {
      if (this.openDelay || this.closeDelay) {
        clearTimeout(this._timer)
      }
    }
  },
  destroyed() {
    const reference = this.reference
    off(reference, 'click', this.doToggle)
    off(reference, 'mouseup', this.doClose)
    off(reference, 'mousedown', this.doShow)
    off(reference, 'focusin', this.doShow)
    off(reference, 'focusout', this.doClose)
    off(reference, 'mousedown', this.doShow)
    off(reference, 'mouseup', this.doClose)
    off(reference, 'mouseleave', this.handleMouseLeave)
    off(reference, 'mouseenter', this.handleMouseEnter)
    off(document, 'click', this.handleDocumentClick)
  }
}
</script>